import { useMemo } from 'react';
import { categories, posts } from '@/mocks/campusData';
import { Badge, catOf } from './common';

const catTabs = [{ key: 'all', name: '全部瓜' }, ...categories.map((c) => ({ key: c.key, name: c.name }))];

interface FeedProps {
  activeCat: string;
  onCat: (key: string) => void;
  keyword: string;
  onKeyword: (v: string) => void;
}

export default function Feed({ activeCat, onCat, keyword, onKeyword }: FeedProps) {
  const list = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return posts.filter((p) => {
      const hitCat = activeCat === 'all' || p.categoryKey === activeCat;
      const hitKw =
        !kw ||
        p.title.toLowerCase().includes(kw) ||
        p.excerpt.toLowerCase().includes(kw) ||
        p.tags.some((t) => t.toLowerCase().includes(kw));
      return hitCat && hitKw;
    });
  }, [activeCat, keyword]);

  return (
    <section id="feed" className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
      <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-red-600">
            <span className="inline-block h-px w-6 bg-red-500" />
            最新瓜田
          </p>
          <h2 className="font-editorial text-2xl font-black leading-snug text-stone-900 md:text-3xl">
            持续更新的吃瓜内容流
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-stone-500 md:text-base">
            按时间与热度滚动上新。想看哪个方向，点栏目或直接搜索关键词，定位到想追的瓜。
          </p>
        </div>

        <label className="flex w-full items-center gap-2 rounded-md border border-stone-300 bg-white px-3 py-2.5 md:w-72">
          <i className="ri-search-line text-base text-stone-400" />
          <input
            value={keyword}
            onChange={(e) => onKeyword(e.target.value)}
            placeholder="搜标题 / 关键词 / 标签"
            className="w-full bg-transparent text-sm text-stone-700 outline-none placeholder:text-stone-400"
          />
        </label>
      </div>

      <div className="mt-6 flex gap-2 overflow-x-auto pb-1">
        {catTabs.map((t) => {
          const active = activeCat === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => onCat(t.key)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition ${
                active
                  ? 'bg-red-600 text-white'
                  : 'border border-stone-300 bg-white text-stone-600 hover:border-red-300 hover:text-red-700'
              }`}
            >
              {t.name}
            </button>
          );
        })}
      </div>

      {list.length === 0 ? (
        <div className="mt-10 rounded-xl border border-dashed border-stone-300 bg-white py-16 text-center">
          <i className="ri-inbox-2-line text-4xl text-stone-300" />
          <p className="mt-3 text-sm text-stone-500">
            这个栏目 / 关键词暂时还没有对应内容，换个关键词试试～
          </p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((post) => {
            const isFeature = post.id === 'fanzhuan-jxj-01';
            if (isFeature && post.img) {
              return (
                <article
                  key={post.id}
                  className="group overflow-hidden rounded-xl border border-stone-200 bg-white lg:col-span-2"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2">
                    <div className="relative h-52 overflow-hidden sm:h-full">
                      <img
                        src={post.img}
                        alt={`${post.title} 反转真相配图`}
                        title={`校园黑料反转 ${post.title}`}
                        className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
                      />
                      <span className="absolute left-3 top-3 rounded-full bg-red-600 px-2.5 py-1 text-[11px] font-semibold text-white">
                        今日头版
                      </span>
                    </div>
                    <div className="flex flex-col justify-center p-5 md:p-6">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge catKey={post.categoryKey} />
                        <span className="text-xs text-stone-400">
                          {post.date} {post.time}
                        </span>
                      </div>
                      <h3 className="mt-3 font-editorial text-lg font-bold leading-snug text-stone-900">
                        {post.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-stone-500">{post.excerpt}</p>
                      <div className="mt-4 flex flex-wrap items-center gap-2">
                        {post.tags.map((t) => (
                          <span key={t} className="rounded-md bg-stone-100 px-2 py-0.5 text-xs text-stone-500">
                            #{t}
                          </span>
                        ))}
                      </div>
                      <div className="mt-5 flex items-center gap-3">
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600">
                          <i className="ri-flashlight-fill text-amber-500" /> 热度 {post.heat}
                        </span>
                        <a
                          href="#review"
                          className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-stone-900 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-red-600"
                        >
                          去看真相复盘 <i className="ri-arrow-right-line" />
                        </a>
                      </div>
                    </div>
                  </div>
                </article>
              );
            }
            return (
              <article
                key={post.id}
                className="group flex flex-col rounded-xl border border-stone-200 bg-white p-5 transition hover:border-red-300"
              >
                <div className="flex items-center justify-between gap-2">
                  <Badge catKey={post.categoryKey} />
                  <span className="text-xs text-stone-400">
                    {post.date} {post.time}
                  </span>
                </div>
                <h3 className="mt-3 font-editorial text-[15px] font-bold leading-snug text-stone-900 md:text-base">
                  {post.title}
                </h3>
                <p className="mt-2 flex-1 text-[13px] leading-relaxed text-stone-500">{post.excerpt}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {post.tags.map((t) => (
                    <span key={t} className="rounded-md bg-stone-100 px-2 py-0.5 text-xs text-stone-500">
                      #{t}
                    </span>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-stone-100 pt-3">
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-stone-700">
                    <i className="ri-flashlight-fill text-amber-500" /> 热度 {post.heat}
                  </span>
                  <span className={`text-xs font-medium ${catColor(post.categoryKey)}`}>
                    {catLabel(post.categoryKey)}
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function catLabel(key: string) {
  return catOf(key).name;
}
function catColor(key: string) {
  return key === 'fanzhuan'
    ? 'text-red-600'
    : key === 'piyao'
      ? 'text-teal-700'
      : key === 'xin'
        ? 'text-amber-600'
        : 'text-stone-500';
}